import axios from 'axios';
import { useState } from 'react';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5001`
    : 'http://localhost:5001');

const API_URL = `${API_BASE.replace(/\/$/, '')}/api`;
const STORAGE_KEY = 'tellurvision_auth';

function normalizeAuthPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const inner =
    payload.user && typeof payload.user === 'object'
      ? ({ ...(payload.user as Record<string, unknown>) } as Record<string, unknown>)
      : ({ ...payload } as Record<string, unknown>);

  if (payload.user && typeof payload.user === 'object') {
    return {
      ...payload,
      user: inner,
    };
  }

  return inner;
}

function extractAxiosMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const e = error as Record<string, unknown>;
  const response = e.response as Record<string, unknown> | undefined;
  const data = response?.data as Record<string, unknown> | undefined;
  const message = data?.message;
  return typeof message === 'string' ? message : null;
}

export const login = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const data = normalizeAuthPayload(response.data as Record<string, unknown>);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      }
    } catch {
      // best-effort; auth still works in-memory even if storage fails
    }
    return data;
  } catch (error) {
    const msg = extractAxiosMessage(error) ?? String(error);
    throw new Error(msg || 'Login failed');
  }
};

// Compatibility alias for admin login
export const loginAdmin = login;

export const logout = async () => {
  try {
    await axios.post(`${API_URL}/auth/logout`);
  } catch (error) {
    // Logout should be best-effort: even if the backend
    // endpoint is unavailable, clearing local auth state
    // on the client is sufficient for JWT-based flows.
    // Swallow the error to avoid confusing the user.
    // Optionally log to console for debugging.

    console.warn('Logout request failed, continuing with local sign-out:', error);
  } finally {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }
};

export const getCurrentUser = async () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = normalizeAuthPayload(JSON.parse(raw) as Record<string, unknown>);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    return parsed;
  } catch {
    return null;
  }
};

export type AuthUser = Record<string, unknown> | null;

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loginUser = async (email: string, password: string) => {
    setLoading(true);
    try {
      const userData = await login(email, password);
      setUser(userData as AuthUser);
    } catch (err: unknown) {
      const msg =
        typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof (err as Record<string, unknown>).message === 'string'
          ? ((err as Record<string, unknown>).message as string)
          : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const logoutUser = async () => {
    await logout();
    setUser(null);
  };

  // Provide named aliases to match existing hooks usage across the app
  return {
    user,
    loading,
    error,
    login: loginUser,
    logout: logoutUser,
    // keep original names available too
    loginUser,
    logoutUser,
  };
};
